// New pricing structure types
export enum PricingTier {
  Basic = "Basic",
  Standard = "Standard", 
  Premium = "Premium",
  PremiumPlus = "Premium Plus",
  EmeraldEliteClub = "Emerald Elite Club"
}

export interface TierConfig {
  tier: PricingTier;
  plantCountRange: string;
  serviceTypes: string[];
  frequencies: string[];
  includedServices: string[];
  monthlyPrice: number;
  isCustomizable: boolean;
}

export interface TierPricingResult {
  tier: PricingTier;
  monthlyPrice: number;
  plantCountRange: string;
  serviceType: string;
  frequency: string;
  includedServices: string[];
  isCustomizable: boolean;
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Service Types
export enum ServiceType {
  Indoor = "Indoor",
  Outdoor = "Outdoor",
  Combined = "Combined"
}

export enum ServiceFrequency {
  Weekly = "Weekly",
  SemiWeekly = "Semi Weekly",
  BiWeekly = "Biweekly",
  BiWeeklyRecurring = "Biweekly Recurring",
  OneTime = "One-Time"
}

export enum PlantRange {
  Range1 = "1-10",
  Range2 = "11-20",
  Range3 = "21-30",
  Range4 = "31-40",
  RangeCustom = "40+"
}

export interface AddOn {
  id: string;
  name: string;
  description: string;
  price: number;
  selected?: boolean;
}

export interface BookingDetails {
  plantCount: PlantRange;
  frequency: ServiceFrequency;
  serviceType: ServiceType;
  address: string;
  accessInfo: string;
  specialInstructions: string;
  price: number;
  pricePerVisit: number;
  totalCommitmentPrice?: number;
  minimumCommitment?: string;
  date?: Date;
  timeSlot?: string;
  zipCode?: string;
  zone?: ServiceZone;
  addOns?: AddOn[];
  agreedToTerms?: boolean;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  address?: string;
  isAdmin?: boolean;
  isTechnician?: boolean;
  profileimage?: string; // Changed from profileImage to profileimage to match the DB column name
  notificationPreferences?: NotificationPreferences;
}

export interface ServiceVisit {
  id: string;
  userId: string;
  technicianId?: string;
  technicianName?: string;
  date: Date;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  plantCount: PlantRange;
  serviceType: ServiceType;
  actualPlantCount?: number;
  checkInTime?: Date;
  checkOutTime?: Date;
  duration?: number;
  notes?: string;
  issues?: VisitIssue[];
  photos?: string[];
  rating?: number;
  feedback?: string;
  address?: string;
  zipCode?: string;
  zone?: ServiceZone;
  timeSlot?: string;
  review?: Review;
}

export interface VisitIssue {
  type: 'pest' | 'disease' | 'under-watering' | 'over-watering' | 'other';
  description: string;
  severity: 'low' | 'medium' | 'high';
}

// Visit Report
export interface VisitReport {
  visitId: string;
  plantsWatered: number;
  timeSpent: number;
  serviceDate: Date;
  issues: VisitIssue[];
  technicianComment: string;
  photos?: string[];
}

// Zone Management
export enum ServiceZone {
  PalmSprings = "Palm Springs",
  CathedralCity = "Cathedral City",
  Other = "Other Areas"
}

export interface ZoneSettings {
  id: string; // Added this field which was missing before
  zone: ServiceZone;
  availableDays: number[]; // 0 = Sunday, 1 = Monday, etc.
  isActive: boolean;
  zipCodes: string[];
  color: string; // for map visualization
}

// Time slots
export type TimeSlot = "earlyMorning" | "evening" | "afternoonReserved";

export interface AvailableSlot {
  date: Date;
  timeSlot: TimeSlot;
  zone: ServiceZone;
  availableSpots: number;
}

// Route Planning
export interface RouteDay {
  date: Date;
  technicians: TechnicianRoute[];
}

export interface TechnicianRoute {
  technicianId: string;
  technicianName: string;
  visits: ServiceVisit[];
  estimatedMileage?: number;
  estimatedTravelTime?: number; // in minutes
}

// New interfaces for notifications, reviews, and calendar

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: NotificationType;
  read: boolean;
  createdAt: Date;
  relatedVisitId?: string;
  link?: string;
}

export enum NotificationType {
  TechEnroute = 'tech-enroute',
  AccessGranted = 'access-granted',
  ReportAvailable = 'report-available',
  ServiceComplete = 'service-complete',
  NewBooking = 'new-booking',
  Reminder = 'reminder',
  System = 'system'
}

export interface NotificationPreferences {
  email: boolean;
  push: boolean;
  emailFrequency?: 'immediate' | 'daily' | 'weekly';
  disabledTypes?: NotificationType[];
}

export interface Review {
  id: string;
  visitId: string;
  userId: string;
  technicianId: string;
  rating: number;
  comment?: string;
  timestamp: Date;
  tipAmount?: number;
}
